import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "../../lib/api";
import PublicNav from "../../components/PublicNav";
import Footer from "../../components/Footer";
import { CheckCircle, Spinner, Warning } from "@phosphor-icons/react";

export default function GiftCardSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ loading: true, status: null, code: null, error: null });

  useEffect(() => {
    if (!sessionId) { setState({ loading: false, error: "Missing session_id" }); return; }
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const r = await api.get(`/gift-cards/status/${sessionId}`);
        if (cancelled) return;
        if (r.data.status !== "pending") {
          setState({ loading: false, status: r.data.status, code: r.data.code });
          return;
        }
        if (attempts < 12) {
          setTimeout(poll, 2000);
        } else {
          setState({ loading: false, status: "pending", code: null });
        }
      } catch (e) {
        if (cancelled) return;
        setState({ loading: false, error: e?.response?.data?.detail || "Could not fetch status" });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="public-shell">
      <PublicNav />
      <section className="bg-ms-cream pt-32 pb-24 md:pt-40 min-h-[70vh]" data-testid="gift-card-success">
        <div className="max-w-2xl mx-auto px-6 md:px-12 text-center">
          {state.loading && (
            <div>
              <Spinner size={36} className="text-ms-brass mx-auto animate-spin" />
              <p className="mt-4 text-ms-stone">Confirming your purchase…</p>
            </div>
          )}
          {!state.loading && state.status && state.status !== "pending" && (
            <div data-testid="gc-success-confirmed">
              <CheckCircle size={56} weight="fill" className="text-ms-brass mx-auto" />
              <h1 className="serif text-4xl md:text-5xl text-ms-ink mt-6">Your gift is on its way.</h1>
              <p className="mt-4 text-ms-stone text-lg leading-relaxed">
                The recipient has just received an email with their Mystery Served code. We've also sent you a receipt.
              </p>
              {state.code && (
                <div className="mt-8 inline-block bg-white border border-ms-edge rounded-md px-6 py-4">
                  <p className="label-eyebrow text-ms-stone text-[10px]">Gift code (also in recipient's email)</p>
                  <p className="serif text-2xl text-ms-ink mt-1 tracking-wider">{state.code}</p>
                </div>
              )}
              <div className="mt-10">
                <Link to="/tours" className="btn-primary">Browse tours</Link>
              </div>
            </div>
          )}
          {!state.loading && state.status === "pending" && (
            <div>
              <Warning size={48} weight="fill" className="text-ms-warn mx-auto" />
              <h1 className="serif text-3xl text-ms-ink mt-6">Payment received — confirmation in progress</h1>
              <p className="mt-4 text-ms-stone">Your payment was successful but we're still finalizing the gift card. You'll receive a confirmation email shortly. You can safely close this page.</p>
            </div>
          )}
          {state.error && (
            <div>
              <Warning size={48} weight="fill" className="text-ms-danger mx-auto" />
              <p className="mt-4 text-ms-stone">{state.error}</p>
              <Link to="/gift-cards" className="mt-6 inline-block text-ms-brass underline">Try again</Link>
            </div>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}
